import { get, isEmpty, isFunction, throttle } from 'lodash-es'
import PropTypes from 'prop-types'
import React, { useRef, useCallback, useLayoutEffect } from 'react'
import ReactDOM from 'react-dom'
import { connect } from 'react-redux'
import { useMount, usePrevious, useUnmount, useUpdateEffect } from 'react-use'

import { CommentsListComponent } from '..'
import {
  CommentsFormComponent,
  InfiniteListComponent,
  LoaderComponent,
} from '../../../../components'
import { useRoomGlobalInfo } from '../../../../../global/blocs/roomInfo'
import { useAskAudienceName } from '../../../../../Chat/components/AskAudienceName/AskAudienceNameModal'
import { useAudienceName } from '../../../../../Chat/blocs/useAudienceName'

import './CommentsContainerComponent.scss'

const doScroll = (loaderWrapper, autoScrollTo) => {
  // eslint-disable-next-line react/no-find-dom-node
  const loaderWrapperNode = ReactDOM.findDOMNode(loaderWrapper.current)
  const destination =
    autoScrollTo === 'top' ? 0 : loaderWrapperNode.scrollHeight

  loaderWrapperNode.scrollTop = destination
}

const getDirection = (displayDirection, commentType) => {
  if (displayDirection !== 'auto') {
    return displayDirection
  }

  return commentType === 'question' ? 'bottom' : 'top'
}

const renderInfiniteScroll = (
  infiniteLoad,
  isInfiniteLoading,
  hasMoreElements
) => {
  return (
    <InfiniteListComponent
      onInfiniteLoad={infiniteLoad}
      isInfiniteLoading={isInfiniteLoading}
      hasMoreElements={hasMoreElements}
    />
  )
}

const updateOnScroll = throttle(
  (commentsList) => {
    commentsList.parentElement.style.setProperty(
      '--scroll-top',
      `${commentsList.scrollTop}px`
    )
    commentsList.parentElement.style.setProperty(
      '--scroll-bottom',
      `${
        commentsList.scrollHeight -
        commentsList.clientHeight -
        commentsList.scrollTop
      }px`
    )
  },
  16,
  { leading: true, trailing: true }
)

const CommentsContainerComponent = ({
  presenterToken,
  includeBotGreeting,
  doNotFetchQuestions,
  init,
  leave,
  comments,
  shouldScroll,
  scrollIntention,
  resetScrollingProps,
  addComment,
  setScrollingIntention,
  displayDirection,
  commentType,
  isSelected,
  autoScrollTo,
  styles,
  infiniteLoad,
  isInfiniteLoading,
  hasMoreElements,
  isLoading,
  canDelete,
  renderSingleComment,
  style,
  unreadIdsList,
  gridSize,
  extraListButtons,
  renderCustomEmptyState,
  onDeleteCommentClick,
  renderListOnTop,
  deleteComment,
  moveComment,
  updateComment,
  isGroupedByStatus,
  canPost,
}) => {
  const { liveId } = useRoomGlobalInfo()

  useMount(() =>
    init(liveId, presenterToken, includeBotGreeting, doNotFetchQuestions)
  )
  useUnmount(leave)

  const previousComments = usePrevious(comments)

  const loaderWrapper = useRef()

  const audienceName = useAudienceName()
  const openAskAudienceNameModal = useAskAudienceName()

  useUpdateEffect(() => {
    if (isEmpty(comments)) {
      return
    }

    if (
      shouldScroll ||
      (scrollIntention && previousComments.length < comments.length)
    ) {
      doScroll(loaderWrapper, autoScrollTo)
      resetScrollingProps()
    }
  }, [comments, shouldScroll])

  const direction = getDirection(displayDirection, commentType)

  const onAddCommentClicked = useCallback(
    async (formValues, formSubmitPromise) => {
      if (!audienceName) {
        const finished = await openAskAudienceNameModal()
        if (!finished) return
      }

      addComment(formValues, formSubmitPromise)

      setScrollingIntention(true)
    },
    [addComment, audienceName, openAskAudienceNameModal, setScrollingIntention]
  )

  const scrollHeight = useRef(null)
  useLayoutEffect(() => {
    // eslint-disable-next-line react/no-find-dom-node
    const lw = ReactDOM.findDOMNode(loaderWrapper.current)
    if (isInfiniteLoading) {
      scrollHeight.current = lw.scrollHeight
    } else if (scrollHeight.current) {
      lw.scrollTop = lw.scrollHeight - scrollHeight.current
    }
  }, [isInfiniteLoading])

  return (
    <div
      className="comments-container-component"
      style={!isSelected ? { display: 'none' } : {}}
    >
      {canPost && !renderListOnTop && (
        <CommentsFormComponent
          form="questionForm"
          onSubmit={onAddCommentClicked}
        />
      )}
      <div className="gradient-holder">
        <div
          ref={loaderWrapper}
          className="chat-questions-list-wrapper"
          onScroll={(scrollEvent) => updateOnScroll(scrollEvent.currentTarget)}
        >
          {isLoading ? (
            <div className="loader-wrapper">
              <LoaderComponent size="large" />
            </div>
          ) : (
            <>
              {direction === 'top' &&
                renderInfiniteScroll(
                  infiniteLoad,
                  isInfiniteLoading,
                  hasMoreElements
                )}
              <CommentsListComponent
                comments={comments}
                commentType={commentType}
                deleteComment={(commentId) => {
                  deleteComment(commentId)
                  if (isFunction(onDeleteCommentClick)) {
                    onDeleteCommentClick(commentId)
                  }
                }}
                moveComment={moveComment}
                updateComment={updateComment}
                canDelete={canDelete}
                renderSingleComment={renderSingleComment}
                allowEmpty={doNotFetchQuestions}
                style={style}
                gridSize={gridSize}
                styles={{
                  singleCommentStyles: get(styles, 'singleCommentStyles', {}),
                }}
                unreadIdsList={unreadIdsList}
                extraButtons={extraListButtons}
                renderCustomEmptyState={renderCustomEmptyState}
                isGroupedByStatus={isGroupedByStatus}
              />
              {direction === 'bottom' &&
                renderInfiniteScroll(
                  infiniteLoad,
                  isInfiniteLoading,
                  hasMoreElements
                )}
            </>
          )}
        </div>
      </div>
      {canPost && renderListOnTop && (
        <CommentsFormComponent
          form="questionForm"
          onSubmit={onAddCommentClicked}
        />
      )}
    </div>
  )
}

CommentsContainerComponent.propTypes = {
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  uuid: PropTypes.string,
  doNotFetchQuestions: PropTypes.bool,
  isLoading: PropTypes.bool,
  init: PropTypes.func,
  deleteComment: PropTypes.func,
  onDeleteCommentClick: PropTypes.func, // external prop passed to the Feature, it does NOT guarantee that comment is deleted before calling it
  addComment: PropTypes.func,
  leave: PropTypes.func,
  comments: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.object),
    PropTypes.objectOf(PropTypes.object),
    PropTypes.object,
  ]),
  canPost: PropTypes.bool,
  infiniteLoad: PropTypes.func,
  isInfiniteLoading: PropTypes.bool,
  hasMoreElements: PropTypes.bool,
  renderListOnTop: PropTypes.bool,
  commentType: PropTypes.oneOf(['question', 'chat']),
  isSelected: PropTypes.bool,
  displayDirection: PropTypes.oneOf(['auto', 'top', 'bottom']),
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  order: PropTypes.oneOf(['desc', 'asc']),
  canDelete: PropTypes.bool,
  renderSingleComment: PropTypes.func,
  renderCustomEmptyState: PropTypes.func,
  presenterToken: PropTypes.string,
  autoScrollTo: PropTypes.oneOf(['top', 'bottom']),
  includeBotGreeting: PropTypes.bool,
  extraListButtons: PropTypes.func,
  style: PropTypes.object,
  styles: PropTypes.shape({
    singleCommentStyles: PropTypes.shape({
      readCommentStyle: PropTypes.object,
      unreadCommentStyle: PropTypes.object,
    }),
    inputStyle: PropTypes.object,
    buttonStyle: PropTypes.object,
  }),
  shouldScroll: PropTypes.bool,
  scrollIntention: PropTypes.bool,
  resetScrollingProps: PropTypes.func,
  setScrollingIntention: PropTypes.func,
  unreadIdsList: PropTypes.object /* ES6 Set */,
  gridSize: PropTypes.number,
  moveComment: PropTypes.func,
  updateComment: PropTypes.func,
  // eslint-disable-next-line react/no-unused-prop-types
  isGroupedByStatus: PropTypes.bool,
}

CommentsContainerComponent.defaultProps = {
  renderListOnTop: false,
  displayDirection: 'auto',
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/default-props-match-prop-types
  order: 'desc',
  comments: [],
  infiniteLoad: () => {},
  isInfiniteLoading: false,
  hasMoreElements: false,
  isSelected: true,
  canPost: true,
}

const mapStateProps = (state, ownProps) => {
  const { getQuestions, getQuestionGroupedByStatus } = ownProps.selectors
  let namespace = get(state, `feature-${ownProps.namespace}`)
  namespace = namespace.questions

  let comments = getQuestions(state)

  if (ownProps.isGroupedByStatus) {
    comments = getQuestionGroupedByStatus(state)
  }

  return {
    comments,
    autoScrollTo: 'top',
    isLoading: namespace.isLoading,
    isInfiniteLoading: namespace.isInfiniteLoading,
    hasMoreElements: namespace.hasMoreElements,
  }
}

const mapDispatchToProps = (_, ownProps) => {
  return {
    init: ownProps.initQuestions,
    addComment: ownProps.addQuestion,
    deleteComment: ownProps.deleteQuestion,
    leave: ownProps.leaveQuestions,
    infiniteLoad: ownProps.infiniteLoadQuestions,
    addReaction: () => {}, // Not implemented for questions
    moveComment: ownProps.moveQuestionComment,
    updateComment: ownProps.updateQuestionComment,
  }
}

export default connect(
  mapStateProps,
  mapDispatchToProps
)(CommentsContainerComponent)

export { CommentsContainerComponent }
